import React, { useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ThemeProvider, ThemeContext } from './contexts/ThemeContext';
import { LanguageProvider, LanguageContext } from './contexts/LanguageContext';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './App.css';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import Hero from './components/Hero';
import Services from './components/Services';
import CustomerStories from './components/CustomerStories';
import OpenSource from './components/OpenSource';
import PartnerLogos from './components/PartnerLogos';

// Import service pages
import PlatformEngineering from './pages/services/PlatformEngineering';
import ManagedOpenShift from './pages/services/ManagedOpenShift';
import KubernetesConsulting from './pages/services/KubernetesConsulting';
import MultiTenantOperator from './pages/services/MultiTenantOperator';
import CustomerStoriesPage from './pages/CustomerStories';
import CaseStudyDetail from './pages/customer-stories/CaseStudyDetail';
import Sustainability from './pages/Sustainability';
import OpenSourceWork from './pages/OpenSource';
import ContactUs from './pages/ContactUs';
import AllServices from './pages/AllServices';
import OpenShiftConsultancy from './pages/OpenShiftConsultancy';
import MonitoringObservability from './pages/MonitoringObservability';
import PlatformReEngineering from './pages/PlatformReEngineering';
import AdvancedKubernetesServices from './pages/AdvancedKubernetesServices';
import ServiceMeshIstio from './pages/ServiceMeshIstio';
import OpenTelemetryObservability from './pages/OpenTelemetryObservability';
import KubernetesPlatformSecurity from './pages/KubernetesPlatformSecurity';
import AdvancedKubernetesNetworking from './pages/AdvancedKubernetesNetworking';
import RedHatCertificationTraining from './pages/RedHatCertificationTraining';
import CloudCertificationTraining from './pages/CloudCertificationTraining';
import FAQs from './pages/FAQs';
import CareersAndJobs from './pages/CareersAndJobs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Support from './pages/Support';

// Import risk consulting pages
import PhysicalSecurity from './pages/risk-consulting/PhysicalSecurity';
import ElectronicsSecurity from './pages/risk-consulting/ElectronicsSecurity';
import FireLifeSafety from './pages/risk-consulting/FireLifeSafety';
import InformationSecurity from './pages/risk-consulting/InformationSecurity';
import CyberSecurity from './pages/risk-consulting/CyberSecurity';
// Error Boundary Component for catching React errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    console.error("React Error Boundary Caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', margin: '20px', backgroundColor: '#ffeeee', color: 'red', border: '1px solid red', borderRadius: '5px' }}>
          <h2>Something went wrong in the React application.</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>See Error Details</summary>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

// Component for the Sustainability section with proper internationalization
function SustainabilitySection() {
  const { translations } = useContext(LanguageContext);
  
  return (
    <div className="py-16 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4 text-center">
        <div data-aos="fade-up">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
            {translations['sustainability.title'] || 'Sustainability Commitment'}
          </h2>
          <p className="text-black dark:text-white max-w-2xl mx-auto mb-6 leading-relaxed font-medium">
            {translations['sustainability.subtitle'] || 'At Fepsec, we\'re committed to promoting sustainable development by aligning our efforts with the UN\'s Sustainable Development Goals 9 and 13, which has become a core value in everything we do.'}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-10">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-l-4 border-blue-500">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full mr-4">
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">9</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                  {translations['sustainability.goal9.title'] || 'Industry, Innovation and Infrastructure'}
                </h3>
              </div>
              <p className="text-black dark:text-white font-medium">
                {translations['sustainability.goal9.description'] || 'Developing resilient infrastructure, promoting inclusive and sustainable industrialization and fostering innovation.'}
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-l-4 border-green-500">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 dark:bg-green-900 p-3 rounded-full mr-4">
                  <span className="text-2xl font-bold text-green-600 dark:text-green-400">13</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                  {translations['sustainability.goal13.title'] || 'Climate Action'}
                </h3>
              </div>
              <p className="text-black dark:text-white font-medium">
                {translations['sustainability.goal13.description'] || 'Taking urgent action to combat climate change and its impacts through technology optimization and education.'}
              </p>
            </div>
          </div>
          
          <Link 
            to="/sustainability" 
            className="inline-block text-primary dark:text-blue-400 font-medium hover:underline"
          >
            {translations['sustainability.learnMore'] || 'Learn More About Our Initiatives'}
          </Link>
        </div>
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      easing: 'ease-out-cubic'
    });
    
    return () => {
      AOS.refresh();
    };
  }, []);
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const { theme } = useContext(ThemeContext);
  
  return (
    <Router>
      <div className="flex flex-col min-h-screen transition-colors duration-300 bg-white dark:bg-gray-900">
        <Header />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={
              <>
                <Hero />
                <PartnerLogos />
                {/* Distinctive separator between sections */}
                <div className="w-full py-12 bg-white dark:bg-gray-900">
                  <div className="container mx-auto">
                    <div className="w-3/4 h-0.5 bg-blue-100 dark:bg-blue-900 mx-auto"></div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-900">
                  <Services />
                </div>
                <div className="bg-white dark:bg-gray-900">
                  <CustomerStories />
                </div>
                <div className="bg-white dark:bg-gray-900">
                  <SustainabilitySection />
                </div>
                <div className="bg-white dark:bg-gray-900">
                  <OpenSource />
                </div>
              </>
            } />
            
            {/* Service Pages */}
            <Route path="/platform-engineering" element={<PlatformEngineering />} />
            <Route path="/managed-openshift" element={<ManagedOpenShift />} />
            <Route path="/kubernetes-consulting" element={<KubernetesConsulting />} />
            <Route path="/multi-tenant-operator" element={<MultiTenantOperator />} />
            
            {/* Other Pages */}
            <Route path="/customer-stories" element={<CustomerStoriesPage />} />
            <Route path="/customer-stories/:slug" element={<CaseStudyDetail />} />
            <Route path="/sustainability" element={<Sustainability />} />
            <Route path="/open-source" element={<OpenSourceWork />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/services" element={<AllServices />} />
            <Route path="/support" element={<Support />} />
            
            {/* Footer-Linked Pages */}
            <Route path="/openshift-consultancy" element={<OpenShiftConsultancy />} />
            <Route path="/monitoring-observability" element={<MonitoringObservability />} />
            <Route path="/platform-re-engineering" element={<PlatformReEngineering />} />
            <Route path="/advanced-kubernetes-services" element={<AdvancedKubernetesServices />} />
            <Route path="/service-mesh-istio" element={<ServiceMeshIstio />} />
            <Route path="/open-telemetry-observability" element={<OpenTelemetryObservability />} />
            <Route path="/observability" element={<MonitoringObservability />} />
            <Route path="/kubernetes-platform-security" element={<KubernetesPlatformSecurity />} />
            <Route path="/advanced-kubernetes-networking" element={<AdvancedKubernetesNetworking />} />
            <Route path="/red-hat-certification-training" element={<RedHatCertificationTraining />} />
            <Route path="/cloud-certification-training" element={<CloudCertificationTraining />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/careers-and-jobs" element={<CareersAndJobs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            
            {/* Risk Consulting Pages */}
            <Route path="/physical-security" element={<PhysicalSecurity />} />
            <Route path="/electronics-security" element={<ElectronicsSecurity />} />
            <Route path="/fire-life-safety" element={<FireLifeSafety />} />
            <Route path="/information-security" element={<InformationSecurity />} />
            <Route path="/cyber-security" element={<CyberSecurity />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
